import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { 
  BookOpen, 
  Database, 
  Route, 
  Component, 
  Palette, 
  Plug, 
  Code,
  ChevronRight,
  Search,
  Copy,
  Check,
  Layers,
  FolderTree,
  Settings,
  Zap,
  Clock,
  Sparkles
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Import registries
import { 
  routesRegistry, 
  storesRegistry, 
  componentsRegistry, 
  designTokens, 
  techStack,
  directoryStructure 
} from '@/docs/registry';
import { changelog, getRecentChanges } from '@/docs/changelog';

// Documentation sections
const sections = [
  { id: 'overview', get label() { return translateInterfaceText("Visão Geral"); }, icon: BookOpen },
  { id: 'changelog', get label() { return translateInterfaceText("Últimas Atualizações"); }, icon: Sparkles },
  { id: 'zustand', get label() { return translateInterfaceText("Arquitetura Zustand"); }, icon: Database },
  { id: 'routes', get label() { return translateInterfaceText("Rotas e Navegação"); }, icon: Route },
  { id: 'components', get label() { return translateInterfaceText("Componentes Principais"); }, icon: Component },
  { id: 'design', get label() { return translateInterfaceText("Design System"); }, icon: Palette },
  { id: 'integrations', get label() { return translateInterfaceText("Integrações"); }, icon: Plug },
  { id: 'development', get label() { return translateInterfaceText("Guia de Desenvolvimento"); }, icon: Code },
];

// Code block component
function CodeBlock({ code, language = 'typescript' }: { code: string; language?: string }) {
  useLanguage();
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group rounded-lg overflow-hidden bg-[#1e1e2e] border border-border/50">
      <div className="flex items-center justify-between px-4 py-2 bg-[#181825] border-b border-border/30">
        <span className="text-xs text-muted-foreground font-mono">{language}</span>
        <button 
          onClick={handleCopy}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm">
        <code className="text-[#cdd6f4] font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );
}

// Section wrapper
function DocSection({ id, title, icon: Icon, children }: { 
  id: string; 
  title: string; 
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  useLanguage();
  return (
    <section id={id} className="scroll-mt-24 mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>
      </div>
      <div className="space-y-6 text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

// Changelog Section (NEW - Dynamic)
function ChangelogSection() {
  useLanguage();
  const recentChanges = getRecentChanges(8);
  
  const typeColors = {
    feature: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    improvement: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    fix: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    refactor: 'bg-violet-500/10 text-violet-600 border-violet-500/30',
  };
  
  const typeLabels = {
    feature: translateInterfaceText("Novo recurso"),
    improvement: translateInterfaceText("Melhoria"),
    fix: translateInterfaceText("Correção"),
    refactor: translateInterfaceText("Refatoração"),
  };
  
  return (
    <div className="space-y-4">
      {recentChanges.map((entry, index) => (
        <div 
          key={index} 
          className="p-4 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors"
        >
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex items-center gap-2">
              <span className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full border",
                typeColors[entry.type]
              )}>
                {typeLabels[entry.type]}
              </span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {entry.date}
              </span>
            </div>
          </div>
          <h4 className="font-semibold text-foreground mb-1">{entry.title}</h4>
          <p className="text-sm text-muted-foreground mb-3">{entry.description}</p>
          <div className="flex flex-wrap gap-1.5">
            {entry.affectedAreas.map((area) => (
              <span 
                key={area} 
                className="px-2 py-0.5 bg-muted rounded text-xs font-mono text-muted-foreground"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Zustand Diagram (Dynamic from registry)
function ZustandDiagram() {
  useLanguage();
  const storeColors: Record<string, string> = {
    emerald: "bg-emerald-500/10 border-emerald-500/30",
    violet: "bg-violet-500/10 border-violet-500/30",
    orange: "bg-orange-500/10 border-orange-500/30",
    pink: "bg-pink-500/10 border-pink-500/30",
    cyan: "bg-cyan-500/10 border-cyan-500/30",
    amber: "bg-amber-500/10 border-amber-500/30",
    red: "bg-red-500/10 border-red-500/30",
    slate: "bg-slate-500/10 border-slate-500/30",
  };
  
  const firstRow = storesRegistry.slice(0, 4);
  const secondRow = storesRegistry.slice(4, 8);
  
  return (
    <div className="p-6 bg-card rounded-xl border border-border overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Header */}
        <div className="text-center mb-8">
          <h3 className="text-lg font-semibold text-foreground mb-2">{translateInterfaceText("Fluxo de Estado - Zustand Stores")}</h3>
          <p className="text-sm text-muted-foreground">{translateInterfaceText("Arquitetura de gerenciamento de estado da HostVSL")}</p>
        </div>
        
        {/* Diagram */}
        <div className="flex flex-col items-center gap-6">
          {/* User Actions */}
          <div className="px-6 py-3 bg-primary/20 rounded-full border-2 border-primary text-primary font-medium">
            {translateInterfaceText("👤 Ações do Usuário")}</div>
          
          <div className="w-px h-8 bg-border" />
          
          {/* React Components */}
          <div className="px-6 py-3 bg-blue-500/20 rounded-lg border border-blue-500/50 text-blue-400">
            {translateInterfaceText("⚛️ Componentes React")}</div>
          
          <div className="w-px h-8 bg-border" />
          
          {/* Zustand Stores Grid - First Row */}
          <div className="grid grid-cols-4 gap-4 w-full max-w-4xl">
            {firstRow.map((store) => (
              <div 
                key={store.name}
                className={cn(
                  "p-4 rounded-lg border text-center",
                  storeColors[store.color] || storeColors.slate
                )}
              >
                <div className="font-mono text-xs text-muted-foreground mb-1">{store.hookName}</div>
                <div className="text-sm font-medium text-foreground">{store.description}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {translateInterfaceText("Atualizado:")}{" "}{store.lastUpdated}
                </div>
              </div>
            ))}
          </div>
          
          {/* Second Row */}
          <div className="grid grid-cols-4 gap-4 w-full max-w-4xl">
            {secondRow.map((store) => (
              <div 
                key={store.name}
                className={cn(
                  "p-4 rounded-lg border text-center",
                  storeColors[store.color] || storeColors.slate
                )}
              >
                <div className="font-mono text-xs text-muted-foreground mb-1">{store.hookName}</div>
                <div className="text-sm font-medium text-foreground">{store.description}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {translateInterfaceText("Atualizado:")}{" "}{store.lastUpdated}
                </div>
              </div>
            ))}
          </div>
          
          <div className="w-px h-8 bg-border" />
          
          {/* Persistence Layer */}
          <div className="flex items-center gap-4">
            <div className="px-4 py-2 bg-yellow-500/20 rounded-lg border border-yellow-500/50 text-yellow-400 text-sm">
              {translateInterfaceText("💾 localStorage (persist)")}</div>
            <div className="px-4 py-2 bg-green-500/20 rounded-lg border border-green-500/50 text-green-400 text-sm">
              {translateInterfaceText("🗄️ Supabase (API)")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Routes diagram (Dynamic from registry)
function RoutesDiagram() {
  useLanguage();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 font-semibold text-foreground">{translateInterfaceText("Rota")}</th>
            <th className="text-left py-3 px-4 font-semibold text-foreground">{translateInterfaceText("Componente")}</th>
            <th className="text-left py-3 px-4 font-semibold text-foreground">{translateInterfaceText("Descrição")}</th>
            <th className="text-left py-3 px-4 font-semibold text-foreground">{translateInterfaceText("Atualizado")}</th>
          </tr>
        </thead>
        <tbody>
          {routesRegistry.map((route) => (
            <tr key={route.path} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-3 px-4 font-mono text-primary">{route.path}</td>
              <td className="py-3 px-4 font-mono text-muted-foreground">{route.component}</td>
              <td className="py-3 px-4 text-muted-foreground">{route.description}</td>
              <td className="py-3 px-4 text-xs text-muted-foreground">{route.lastUpdated}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Directory structure (from registry)
function DirectoryTree() {
  useLanguage();
  return (
    <div className="font-mono text-sm bg-card rounded-lg border border-border p-4">
      <pre className="text-muted-foreground">
        {directoryStructure}
      </pre>
    </div>
  );
}

// Components Grid (Dynamic from registry)
function ComponentsGrid() {
  useLanguage();
  return (
    <div className="grid gap-4 mt-6">
      {Object.entries(componentsRegistry).map(([group, components]) => (
        <div key={group} className="p-4 bg-card rounded-lg border border-border">
          <h4 className="font-semibold text-foreground mb-1 capitalize">{group} {" "}{translateInterfaceText("Components")}</h4>
          <p className="text-xs text-muted-foreground mb-3 font-mono">{translateInterfaceText("src/components/")}{group}/</p>
          <div className="flex flex-wrap gap-2">
            {components.map((comp) => (
              <span 
                key={comp.name} 
                className="px-2 py-1 bg-muted rounded text-xs font-mono"
                title={translateInterfaceText("{value0} • Atualizado: {value1}", { value0: (comp.description), value1: (comp.lastUpdated) })}
              >
                {comp.name}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Design Tokens Section (Dynamic from registry)
function DesignTokensSection() {
  useLanguage();
  return (
    <>
      <h3 className="text-lg font-semibold text-foreground mt-6 mb-4">{translateInterfaceText("Cores Principais")}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(designTokens.colors).map(([name, token]) => (
          <div key={name} className="p-4 bg-card rounded-lg border border-border">
            <div 
              className="w-full h-12 rounded-lg mb-3 border border-border"
              style={{ backgroundColor: token.value }}
            />
            <div className="font-semibold text-foreground text-sm capitalize">{name}</div>
            <div className="text-xs text-muted-foreground font-mono">{token.value}</div>
            <div className="text-[10px] text-muted-foreground mt-1">
              {translateInterfaceText("Atualizado:")}{" "}{token.lastUpdated}
            </div>
          </div>
        ))}
      </div>
      
      <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Sombras")}</h3>
      <div className="grid gap-3">
        {Object.entries(designTokens.shadows).map(([name, token]) => (
          <div key={name} className="flex items-center gap-4 p-3 bg-card rounded-lg border border-border">
            <div 
              className="w-16 h-16 bg-background rounded-lg"
              style={{ boxShadow: token.value }}
            />
            <div>
              <div className="font-semibold text-foreground capitalize">{name}</div>
              <div className="text-xs text-muted-foreground font-mono">{token.value}</div>
              <div className="text-xs text-muted-foreground">{token.description}</div>
            </div>
          </div>
        ))}
      </div>
      
      <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Border Radius")}</h3>
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(designTokens.borderRadius).map(([name, token]) => (
          <div key={name} className="p-4 bg-card rounded-lg border border-border text-center">
            <div 
              className="w-16 h-16 bg-primary/20 border-2 border-primary mx-auto mb-3"
              style={{ borderRadius: token.value }}
            />
            <div className="font-semibold text-foreground capitalize">{name}</div>
            <div className="text-xs text-muted-foreground font-mono">{token.value}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// Tech Stack Grid (Dynamic from registry)
function TechStackGrid() {
  useLanguage();
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
      {techStack.map((tech) => (
        <div key={tech.name} className="p-4 bg-card rounded-lg border border-border">
          <div className="font-semibold text-foreground">{tech.name}</div>
          <div className="text-xs text-muted-foreground">{tech.description}</div>
        </div>
      ))}
    </div>
  );
}

export default function Documentation() {
  useLanguage();
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  
  const scrollToSection = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Get last update date from changelog
  const lastUpdate = changelog[0]?.date || new Date().toLocaleDateString(getInterfaceLanguage());
  
  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card/50 flex-shrink-0 hidden lg:block">
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder={translateInterfaceText("Buscar...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-background"
            />
          </div>
        </div>
        <ScrollArea className="h-[calc(100%-65px)]">
          <nav className="p-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                  activeSection === section.id 
                    ? "bg-primary/10 text-primary font-medium" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <section.icon className="w-4 h-4" />
                {section.label}
                {activeSection === section.id && (
                  <ChevronRight className="w-4 h-4 ml-auto" />
                )}
              </button>
            ))}
          </nav>
        </ScrollArea>
      </aside>
      
      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">{translateInterfaceText("Documentação HostVSL")}</h1>
                <p className="text-muted-foreground">
                  {translateInterfaceText("Guia técnico completo • Última atualização:")}{" "}{lastUpdate}
                </p>
              </div>
            </div>
          </div>
          
          {/* Overview Section */}
          <DocSection id="overview" title={translateInterfaceText("Visão Geral da Plataforma")} icon={BookOpen}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("HostVSL é uma plataforma de hospedagem e gerenciamento de VSLs (Video Sales Letters) construída com tecnologias modernas para oferecer a melhor experiência em reprodução de vídeos de vendas.")}</p>
            
            <TechStackGrid />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4 flex items-center gap-2">
              <FolderTree className="w-5 h-5" />
              {translateInterfaceText("Estrutura de Diretórios")}</h3>
            <DirectoryTree />
          </DocSection>
          
          {/* Changelog Section (NEW) */}
          <DocSection id="changelog" title={translateInterfaceText("Últimas Atualizações")} icon={Sparkles}>
            <p className="text-foreground leading-relaxed mb-6">
              {translateInterfaceText("Histórico de melhorias e novas funcionalidades implementadas na plataforma. Esta seção é atualizada automaticamente quando novas features são adicionadas.")}</p>
            <ChangelogSection />
          </DocSection>
          
          {/* Zustand Section */}
          <DocSection id="zustand" title={translateInterfaceText("Arquitetura Zustand")} icon={Database}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("A HostVSL utiliza Zustand para gerenciamento de estado global. Cada store é responsável por um domínio específico da aplicação, com persistência automática via localStorage quando necessário.")}</p>
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5" />
              {translateInterfaceText("Diagrama de Fluxo")}</h3>
            <ZustandDiagram />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Stores Principais")}</h3>
            
            <div className="space-y-4">
              {storesRegistry.slice(0, 3).map((store) => (
                <div key={store.name} className="p-4 bg-card rounded-lg border border-border">
                  <h4 className="font-semibold text-foreground mb-2">{store.hookName}</h4>
                  <p className="text-sm mb-2">{store.description}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="font-mono">{store.path}</span>
                    <span>•</span>
                    <span>{translateInterfaceText("Persistência:")}{" "}{store.hasPersistence ? '✅' : '❌'}</span>
                    <span>•</span>
                    <span>{translateInterfaceText("Atualizado:")}{" "}{store.lastUpdated}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Padrão de Uso")}</h3>
            <CodeBlock 
              code={`// Exemplo de uso em componente React
import { useVideoDashboardStore } from '@/store/videoDashboard/store';

function VideoList() {
  // Seleciona apenas o que precisa (melhor performance)
  const videos = useVideoDashboardStore(state => state.videos);
  const addVideo = useVideoDashboardStore(state => state.addVideo);
  
  // Ou desestrutura tudo (cuidado com re-renders)
  const { videos, folders, addVideo } = useVideoDashboardStore();
  
  return (
    <div>
      {videos.map(video => (
        <VideoCard key={video.id} video={video} />
      ))}
    </div>
  );
}`}
            />
          </DocSection>
          
          {/* Routes Section */}
          <DocSection id="routes" title={translateInterfaceText("Rotas e Navegação")} icon={Route}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("O sistema de rotas é gerenciado pelo React Router v6. Todas as rotas principais são protegidas pelo componente ProtectedRoute que verifica autenticação.")}</p>
            
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-4">{translateInterfaceText("Mapa de Rotas")}</h3>
            <RoutesDiagram />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Rotas Protegidas")}</h3>
            <CodeBlock 
              code={`// src/App.tsx
<Routes>
  {/* Rota pública */}
  <Route path="/auth" element={<AuthPage />} />
  
  {/* Rotas protegidas */}
  <Route path="/" element={
    <ProtectedRoute>
      <MyVideosV2 />
    </ProtectedRoute>
  } />
  
  {/* Redirect de compatibilidade */}
  <Route path="/dashboard" element={<Navigate to="/" replace />} />
</Routes>`}
            />
          </DocSection>
          
          {/* Components Section */}
          <DocSection id="components" title={translateInterfaceText("Componentes Principais")} icon={Component}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("A aplicação utiliza uma arquitetura de componentes modular, com componentes reutilizáveis organizados por domínio.")}</p>
            
            <ComponentsGrid />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Exemplo: StatsCard")}</h3>
            <CodeBlock 
              code={`// src/components/dashboard/StatsCard.tsx
interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  trend?: { value: number; isPositive: boolean };
  onClick?: () => void;
}

export function StatsCard({ title, value, icon: Icon, trend, onClick }: StatsCardProps) {
  return (
    <Card 
      className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className="w-8 h-8 text-primary" />
      </div>
    </Card>
  );
}`}
            />
          </DocSection>
          
          {/* Design System Section */}
          <DocSection id="design" title={translateInterfaceText("Design System")} icon={Palette}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("A HostVSL segue um design system premium inspirado na Apple, com foco em minimalismo, elegância e consistência visual.")}</p>
            
            <DesignTokensSection />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Tipografia")}</h3>
            <CodeBlock 
              code={`/* Fonte principal: Inter */
font-family: 'Inter', sans-serif;

/* Tamanhos */
text-xs   → 12px
text-sm   → 14px
text-base → 16px
text-lg   → 18px
text-xl   → 20px
text-2xl  → 24px
text-3xl  → 30px

/* Pesos */
font-normal    → 400
font-medium    → 500  (números principais)
font-semibold  → 600
font-bold      → 700`}
              language="css"
            />
          </DocSection>
          
          {/* Integrations Section */}
          <DocSection id="integrations" title={translateInterfaceText("Integrações")} icon={Plug}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("A plataforma integra com Supabase para backend (autenticação, banco de dados, storage) e utiliza React Query para cache e sincronização de dados.")}</p>
            
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-4">{translateInterfaceText("Supabase")}</h3>
            <CodeBlock 
              code={`// src/integrations/supabase/client.ts
import { createClient } from '@supabase/supabase-js';

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Exemplo de uso
const { data, error } = await supabase
  .from('videos')
  .select('*')
  .eq('user_id', userId);`}
            />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("React Query")}</h3>
            <CodeBlock 
              code={`// src/hooks/useVideos.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function useVideos() {
  return useQuery({
    queryKey: ['videos'],
    queryFn: fetchVideos,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

export function useCreateVideo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createVideo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['videos'] });
    },
  });
}`}
            />
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Contexts")}</h3>
            <div className="space-y-3">
              {[
                { name: 'ThemeContext', desc: translateInterfaceText("Gerencia tema light/dark/auto") },
                { name: 'TestModeContext', desc: translateInterfaceText("Modo de teste para demonstrações") },
                { name: 'SupportTokenContext', desc: translateInterfaceText("Token de suporte para debug") },
              ].map((ctx) => (
                <div key={ctx.name} className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
                  <Settings className="w-5 h-5 text-primary" />
                  <div>
                    <div className="font-medium text-foreground">{ctx.name}</div>
                    <div className="text-sm text-muted-foreground">{ctx.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </DocSection>
          
          {/* Development Guide Section */}
          <DocSection id="development" title={translateInterfaceText("Guia de Desenvolvimento")} icon={Code}>
            <p className="text-foreground leading-relaxed">
              {translateInterfaceText("Guia prático para desenvolver novas features na HostVSL seguindo os padrões estabelecidos.")}</p>
            
            <h3 className="text-lg font-semibold text-foreground mt-6 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5" />
              {translateInterfaceText("Criando uma Nova Feature")}</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">1</span>
                  <h4 className="font-semibold text-foreground">{translateInterfaceText("Criar o Store (se necessário)")}</h4>
                </div>
                <CodeBlock 
                  code={`// src/store/myFeature/store.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface MyFeatureState {
  data: MyData[];
  addItem: (item: MyData) => void;
}

export const useMyFeatureStore = create(
  persist<MyFeatureState>(
    (set) => ({
      data: [],
      addItem: (item) => set((state) => ({ 
        data: [...state.data, item] 
      })),
    }),
    { name: 'my-feature-storage' }
  )
);`}
                />
              </div>
              
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">2</span>
                  <h4 className="font-semibold text-foreground">{translateInterfaceText("Atualizar Documentação")}</h4>
                </div>
                <CodeBlock 
                  code={`// src/docs/registry.ts
// Adicionar nova rota
routesRegistry.push({
  path: '/my-feature',
  component: 'MyFeature',
  description: 'Minha nova feature',
  lastUpdated: '2025-12-06',
  category: 'other'
});

// src/docs/changelog.ts
// Adicionar entrada no changelog
changelog.unshift({
  date: '2025-12-06',
  title: 'Nova Feature Implementada',
  description: 'Descrição da feature...',
  affectedAreas: ['MyFeature', 'MyStore'],
  type: 'feature'
});`}
                />
              </div>
              
              <div className="p-4 bg-card rounded-lg border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">3</span>
                  <h4 className="font-semibold text-foreground">{translateInterfaceText("Criar Componentes e Página")}</h4>
                </div>
                <CodeBlock 
                  code={`// src/pages/MyFeature.tsx
import { useMyFeatureStore } from '@/store/myFeature/store';

export default function MyFeature() {
  const data = useMyFeatureStore(state => state.data);
  
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">My Feature</h1>
      {/* ... */}
    </div>
  );
}`}
                />
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-foreground mt-8 mb-4">{translateInterfaceText("Comandos Úteis")}</h3>
            <CodeBlock 
              code={`# Desenvolvimento
npm run dev          # Inicia servidor de desenvolvimento
npm run build        # Build de produção
npm run preview      # Preview do build

# Testes
npm run test         # Executa testes
npm run test:watch   # Testes em modo watch

# Lint
npm run lint         # Verifica código
npm run lint:fix     # Corrige automaticamente`}
              language="bash"
            />
          </DocSection>
          
          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("HostVSL Documentation v3.0 (Dinâmica) • Última atualização:")}{" "}{lastUpdate}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {routesRegistry.length} {" "}{translateInterfaceText("rotas •")}{" "}{storesRegistry.length} {" "}{translateInterfaceText("stores •")}{" "}{changelog.length} {" "}{translateInterfaceText("atualizações registradas")}</p>
          </div>
        </div>
      </main>
    </div>
  );
}
